// DEPENDENCIES ---------------------------------------------------------------- //

// import React from 'react';

import { Moves } from '@gql/base-types';

import { TileProps } from '@components/Timeline/Tile/Tile';

import ConciergeDrive from '@components/Timeline/Tile/ConciergeDrive';
import LoanerDrive from '@components/Timeline/Tile/LoanerDrive';
import OpsDrive from '@components/Timeline/Tile/OpsDrive';

import AutoRide from '@components/Timeline/Tile/AutoRide';
import LyftRide from '@components/Timeline/Tile/LyftRide';
import ShuttleRide from '@components/Timeline/Tile/ShuttleRide';
import UberRide from '@components/Timeline/Tile/UberRide';

// TYPES ---------------------------------------------------------------- //

interface MoveTileProps extends TileProps {
  move: Moves;
  size?: 'compressed' | 'small' | 'medium' | 'large';
  zIndex?: number;
  zoom?: number;
  // Enrichment props for visual delay segments
  pickupDelayMin?: number;
  driveDelayMin?: number;
  deliveryDelayMin?: number;
  isInProgress?: boolean;
}

// COMPONENT ---------------------------------------------------------------- //

const MoveTile = ({
  move,
  size = 'medium',
  zIndex = 0,
  zoom = 4,
  pickupDelayMin = 0,
  driveDelayMin = 0,
  deliveryDelayMin = 0,
  isInProgress = false,
  ...rest
}: MoveTileProps) => {
  // Helper Functions //

  /** Get the customer name */
  const getCustomerName = () => {
    if (move?.customer?.name) return move?.customer?.name;
    return 'Unknown';
  };

  /** Get the lane duration in minutes (planned/estimated - delays shown separately via extraWidth) */
  const getLaneDurationMinutes = () => {
    if (move?.move_type === 'ride')
      return (
        ((move?.lane?.duration_sec || 0) -
          (move?.lane?.pickup_inspection_sec || 0) -
          (move?.lane?.delivery_inspection_sec || 0) +
          (move?.lane?.return_ride_wait_sec || 0)) /
        60
      );

    return (move?.lane?.duration_sec || 0) / 60;
  };

  /** Get the lane pickup name */
  const getLanePickupName = () => {
    if (move?.lane?.pickup?.name) return move?.lane?.pickup?.name;
    if (move?.lane?.pickup?.address) return move?.lane?.pickup?.address?.split(',')[0];
    if (move?.lane?.description) return move?.lane?.description?.split(' to ')[0];
    return 'Unknown';
  };

  /** Get the lane delivery name */
  const getLaneDeliveryName = () => {
    if (move?.lane?.delivery?.name) return move?.lane?.delivery?.name;
    if (move?.lane?.delivery?.address) return move?.lane?.delivery?.address?.split(',')[0];
    if (move?.lane?.description) return move?.lane?.description?.split(' to ')[1];
    return 'Unknown';
  };

  // Rendering Logic //

  if (move?.move_type === 'drive') {
    if (move?.consumer_type === 'customer')
      return (
        <ConciergeDrive
          {...rest}
          primaryText1={getLanePickupName()}
          primaryText2={getLaneDeliveryName()}
          secondaryText={getCustomerName()}
          size={size}
          width={getLaneDurationMinutes()}
          extraWidthBefore={pickupDelayMin}
          extraWidthBetween={driveDelayMin}
          extraWidthAfter={deliveryDelayMin}
          zIndex={zIndex}
          zoom={zoom}
        />
      );
    if (move?.consumer_type === 'loaner')
      return (
        <LoanerDrive
          {...rest}
          primaryText1={getLanePickupName()}
          primaryText2={getLaneDeliveryName()}
          secondaryText={getCustomerName()}
          size={size}
          width={getLaneDurationMinutes()}
          extraWidthBefore={pickupDelayMin}
          extraWidthBetween={driveDelayMin}
          extraWidthAfter={deliveryDelayMin}
          zIndex={zIndex}
          zoom={zoom}
        />
      );
    return (
      <OpsDrive
        {...rest}
        primaryText1={getLanePickupName()}
        primaryText2={getLaneDeliveryName()}
        secondaryText={getCustomerName()}
        size={size}
        width={getLaneDurationMinutes()}
        extraWidthBefore={pickupDelayMin}
        extraWidthBetween={driveDelayMin}
        extraWidthAfter={deliveryDelayMin}
        zIndex={zIndex}
        zoom={zoom}
      />
    );
  }

  if (move?.move_type === 'ride') {
    if (move?.ride_type === 'auto')
      return (
        <AutoRide
          {...rest}
          primaryText1={getLanePickupName()}
          primaryText2={getLaneDeliveryName()}
          size={size}
          width={getLaneDurationMinutes()}
          extraWidthBefore={pickupDelayMin}
          extraWidthBetween={driveDelayMin}
          extraWidthAfter={deliveryDelayMin}
          zIndex={zIndex}
          zoom={zoom}
        />
      );
    if (move?.ride_type === 'lyft')
      return (
        <LyftRide
          {...rest}
          primaryText1={getLanePickupName()}
          primaryText2={getLaneDeliveryName()}
          size={size}
          width={getLaneDurationMinutes()}
          extraWidthBefore={pickupDelayMin}
          extraWidthBetween={driveDelayMin}
          extraWidthAfter={deliveryDelayMin}
          zIndex={zIndex}
          zoom={zoom}
        />
      );
    if (move?.ride_type === 'shuttle')
      return (
        <ShuttleRide
          {...rest}
          primaryText1={getLanePickupName()}
          primaryText2={getLaneDeliveryName()}
          size={size}
          width={getLaneDurationMinutes()}
          extraWidthBefore={pickupDelayMin}
          extraWidthBetween={driveDelayMin}
          extraWidthAfter={deliveryDelayMin}
          zIndex={zIndex}
          zoom={zoom}
        />
      );
    if (move?.ride_type === 'uber')
      return (
        <UberRide
          {...rest}
          primaryText1={getLanePickupName()}
          primaryText2={getLaneDeliveryName()}
          size={size}
          width={getLaneDurationMinutes()}
          extraWidthBefore={pickupDelayMin}
          extraWidthBetween={driveDelayMin}
          extraWidthAfter={deliveryDelayMin}
          zIndex={zIndex}
          zoom={zoom}
        />
      );
  }

  return null;
};

// EXPORT ---------------------------------------------------------------- //

export default MoveTile;
